/**
 * WS-S / AUD-8 LB-9(c): what the compositor ACTUALLY did, as one sentence.
 *
 * The panel used to read `backendInfo`, which answers "does an adapter exist and does the
 * preference allow it" — and rendered that as *"Using: GPU (…) for image warping"*. On a template
 * with a spot-UV varnish layer that sentence was false for every warp in the render, because
 * `DeterministicScope` had forced the whole compose onto the CPU and `compositor_info().last_path`
 * was empty. WS-I made `backendInfo` honest about the PREFERENCE; this is the next layer of the
 * same lie: **availability is not use.**
 *
 * Pure, so all five states (R0–R4) are a table test rather than five renders.
 */
import { WEB_COMPOSITOR_REASON, type CompositorInfo } from "@genz/engine-bind";

export type CompositorReadout =
  /** R0: nothing has rendered yet this session. */
  | { state: "idle" }
  /** R1: the last warp ran on the GPU. */
  | { state: "gpu"; adapter: string; backend: string }
  /** R2–R4: the last warp ran on the CPU, and `reason` says why. */
  | { state: "cpu"; reason: string; detail: string };

/**
 * The engine's `last_fallback_reason` strings, in words.
 *
 * `satisfies Record<FallbackReason, string>` makes this a CENSUS: add a reason to
 * `gpu.rs::CompositorInfo` without adding a row here and `tsc` fails. A `default:` branch would
 * instead have rendered a new engine reason as silence — which is how the last one went unnoticed.
 */
export type FallbackReason =
  | "varnish-deterministic"
  | "disabled"
  | "no-adapter"
  | "device-creation-failed"
  | "software-adapter"
  | "error"
  | "blank-output"
  | "degenerate-homography"
  | "empty-input"
  /**
   * The WEB build's answer (`engine-bind/web-capabilities.ts`). Not an engine fallback at all: the
   * wasm crate is compiled without the `gpu` feature, so there is no compositor to fall back FROM.
   * Spelled as `typeof` the shipped constant so a rename there is a `tsc` error here rather than a
   * silent drop into the graceful-unknown branch — which is exactly what the rebase onto WS-N
   * would otherwise have shipped (review I-4).
   */
  | typeof WEB_COMPOSITOR_REASON;

export const FALLBACK_DETAIL = {
  "varnish-deterministic":
    "this template's spot-UV varnish layer forces identical output on every device",
  disabled: "GPU acceleration is off (your setting)",
  "no-adapter": "no compatible GPU adapter was found on this machine",
  "device-creation-failed":
    "a GPU adapter was found but its driver refused to create a device — check for a graphics driver update",
  "software-adapter":
    "the only graphics adapter on this machine is a software renderer, which is slower than the CPU path — so the CPU is used",
  error: "the GPU driver reported an error, so rendering fell back to the CPU",
  "blank-output":
    "the GPU returned an empty image, so rendering fell back to the CPU (a known driver defect)",
  "degenerate-homography": "this layer's placement had no valid transform",
  "empty-input": "there was nothing to warp",
  // Deliberately NOT phrased as a fallback — see the type above. It states the build, because
  // that is the only thing an operator on web can act on (install the desktop app).
  [WEB_COMPOSITOR_REASON]: "this is the browser build, which has no GPU compositor to fall back from",
} satisfies Record<FallbackReason, string>;

/**
 * Classify a `compositor_info()` snapshot.
 *
 * `null` and an empty `lastPath` are BOTH idle. There is deliberately no fall-back to
 * `backendInfo.backend` here: that is the conflation this module exists to undo, and R0 — "nothing
 * has rendered yet" — is the state a user sees the moment they open Settings.
 */
export function compositorReadout(info: CompositorInfo | null | undefined): CompositorReadout {
  if (!info || !info.lastPath) return { state: "idle" };
  if (info.lastPath === "gpu") {
    return {
      state: "gpu",
      adapter: info.adapterName ?? "unknown adapter",
      backend: info.backend ?? "unknown backend",
    };
  }
  const reason = info.lastFallbackReason ?? "";
  return { state: "cpu", reason, detail: detailFor(reason) };
}

/**
 * An UNKNOWN reason names its raw value rather than throwing or rendering nothing — a future
 * engine string must degrade to something an operator can quote in a support request, not to
 * `undefined`. (The census above is what keeps this branch unreachable for reasons we ship.)
 */
function detailFor(reason: string): string {
  const known = (FALLBACK_DETAIL as Record<string, string | undefined>)[reason];
  if (known) return known;
  return reason
    ? `rendering fell back to the CPU (${reason})`
    : "rendering fell back to the CPU";
}

/** The sentence the panel shows. R0 says nothing about the GPU, on purpose. */
export function readoutSentence(r: CompositorReadout): string {
  switch (r.state) {
    case "idle":
      return "Last render: nothing has rendered yet in this session.";
    case "gpu":
      return `Last render: GPU (${r.adapter} / ${r.backend}).`;
    case "cpu":
      return `Last render: CPU — ${r.detail}.`;
  }
}

/**
 * The CAPABILITY line — what this machine HAS, from `backendInfo`. A different question from
 * `readoutSentence`, which is what the last render DID, and keeping them apart is the whole design.
 *
 * `info.backend` is DELIBERATELY unread (review M-4): it answers "will the next warp use the GPU",
 * which is `readoutSentence`'s question, and mixing it in here is how *"available but idle"* — the
 * availability-as-use conflation — got into the old single sentence. `settings-panel.test.tsx`
 * pins the consequence: this line is identical for `backend: "cpu"` and `backend: "gpu"`.
 *
 * It is not dead, and WS-I's fix to it is not orphaned: `NativeEngineBinding` derives `backendId`
 * from `backend` + `gpuAdapter` (`engine-bind/src/native.ts:278`), which scopes the layered path's
 * contentHash, and `engine/genz-engine/tests/backend_truth.rs` pins the field itself. Do not delete
 * it as unused on the strength of this module.
 */
export function capabilitySentence(
  info: { backend: string; threads: number; gpuAdapter: string | null } | null,
): string {
  if (!info) return "";
  const threads = `${info.threads} CPU thread${info.threads === 1 ? "" : "s"}`;
  return info.gpuAdapter
    ? `This machine: GPU (${info.gpuAdapter}) + ${threads}.`
    : `This machine: ${threads}, no compatible GPU adapter.`;
}
